import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Resolver, useForm } from 'react-hook-form';

import { createTaxInfo, getTaxInfo } from '@/api/tax-info';

import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';

import { USTaxReturnFormValues } from '../utils/types';
import {
  getUsTaxReturnValidationSchema,
  isUsTaxReturnFormValid,
} from './schema';
import {
  buildUsTaxReturnRequestPayload,
  DEFAULT_US_TAX_RETURN_FORM_VALUES,
  getUsTaxEntry,
  mapUsTaxEntryToFormValues,
} from './utils';

type UseUsTaxReturnQuestionFormOptions = {
  hasUsIndicia: boolean;
  onAnswered: () => void;
};

export function useUsTaxReturnQuestionForm({
  hasUsIndicia,
  onAnswered,
}: UseUsTaxReturnQuestionFormOptions) {
  const queryClient = useQueryClient();

  const validationSchema = useMemo(
    () => getUsTaxReturnValidationSchema({ hasUsIndicia }),
    [hasUsIndicia],
  );

  const { data: taxInfo } = useQuery({
    queryKey: ['taxInfo'],
    queryFn: getTaxInfo,
  });

  const savedFormValues = useMemo(
    () => mapUsTaxEntryToFormValues(getUsTaxEntry(taxInfo)),
    [taxInfo],
  );

  const {
    control,
    watch,
    resetField,
    handleSubmit,
    formState: { errors },
  } = useForm<USTaxReturnFormValues>({
    resolver: yupResolver(validationSchema) as Resolver<USTaxReturnFormValues>,
    defaultValues: DEFAULT_US_TAX_RETURN_FORM_VALUES,
    values: savedFormValues,
    resetOptions: {
      keepDirtyValues: true,
    },
    mode: 'onChange',
  });

  const usTaxReturnRequired = watch('usTaxReturnRequired');
  const taxNumberTypeCode = watch('taxNumberTypeCode');
  const usTaxNumber = watch('usTaxNumber');
  const usTaxNoReturnReasonCode = watch('usTaxNoReturnReasonCode');
  const usTaxNoReturnReasonOther = watch('usTaxNoReturnReasonOther');

  const canSubmit = isUsTaxReturnFormValid(
    {
      usTaxReturnRequired,
      taxNumberTypeCode,
      usTaxNumber,
      usTaxNoReturnReasonCode,
      usTaxNoReturnReasonOther,
    },
    { hasUsIndicia },
  );

  const { mutate, isPending, error } = useMutationWithUserData({
    mutationFn: (formValues: USTaxReturnFormValues) => {
      const payload = buildUsTaxReturnRequestPayload(formValues, taxInfo);

      return createTaxInfo(payload);
    },
    onFinished: () => {
      queryClient.invalidateQueries({ queryKey: ['taxInfo'] });
      onAnswered();
    },
  });

  const handleTaxReturnChange = (
    selectedValue: string | null,
    onChange: (value: boolean) => void,
  ) => {
    if (selectedValue === null) {
      return;
    }

    const isRequired = selectedValue === 'true';
    onChange(isRequired);

    if (isRequired) {
      resetField('usTaxNoReturnReasonCode', { defaultValue: '' });
      resetField('usTaxNoReturnReasonOther', { defaultValue: '' });
      return;
    }

    resetField('taxNumberTypeCode', { defaultValue: undefined });
    resetField('usTaxNumber', { defaultValue: '' });
  };

  const onSubmit = handleSubmit(formValues => {
    mutate(formValues);
  });

  return {
    control,
    errors,
    canSubmit,
    isPending,
    error,
    onSubmit,
    handleTaxReturnChange,
    resetField,
    usTaxReturnRequired,
    taxNumberTypeCode,
  };
}
